import {
  FC,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { Button } from '@kizen/kds/Button';
import { DEFAULT_KICODE_URL } from 'ts-components/PiChat/connectionDefaults';
import { useBuilderChat } from 'ts-components/PiChat/builder/useBuilderChat';
import { BuilderChatPanel } from 'ts-components/PiChat/builder/BuilderChatPanel';
import { useBusinessApps } from 'ts-components/Plugins/businessApps/useBusinessApps';
import { PanelHeader } from 'ts-components/PiChat/styles';
import { getAuthTeamMember } from 'store/authentication/selectors';
import type { GuardBusy } from 'ts-components/PiChat/PiChatPanel';
import type { ConnectionState } from 'ts-components/PiChat/reducer';
import { CaptureToolRow } from 'ts-components/PiChat/capture/CaptureToolRow';
import {
  CAPTURE_CAPABILITY,
  NAVIGATE_CAPABILITY,
  REFRESH_PLUGINS_CAPABILITY,
} from 'ts-components/PiChat/protocol';
import { DEFAULT_HEIGHT as NAV_BAR_HEIGHT } from 'app/navBarController';
import { useKiCodeNavigation } from '../navigation/useKiCodeNavigation';
import { useKiCodeRefreshPlugins } from '../plugins/useKiCodeRefreshPlugins';
import { useKiCodeCapture } from '../capture/useKiCodeCapture';
import { useKiCodeOpenFloatingFrame } from '../floatingFrames/useKiCodeOpenFloatingFrame';
import { hasKiCodeCapability } from '../profiles';
import { KICODE_FILES_WARNING, kiCodeComposerCopy, kiCodeCopy } from '../copy';
import type { KiCodeSessionState } from '../kiCodeState';
import { boundAppApiName, createKiCodeStart } from './sessionStart';
import { createPromptDelivery } from './promptDelivery';
import { gatedSend } from './gatedSend';
import { clearKiCodeResume, type KiCodeResume } from './resumeStorage';
import {
  businessAppLookupName,
  useBusinessAppResume,
} from './businessAppResume';
import { ResumePendingPanel } from './ResumePendingPanel';
import { kiCodeSessionSurface } from './sessionSurface';

const QA_PREFIX = 'kicode-panel';

const KiCodeChatPanel = styled(BuilderChatPanel)`
  & ${PanelHeader} {
    height: ${NAV_BAR_HEIGHT}px;
  }
`;

export interface KiCodeSessionProps {
  session: KiCodeSessionState;
  onClose: () => void;
  onStartNewConversation: () => void;
  onAppCreated: (appApiName: string) => void;
  onResumable: (resume: KiCodeResume) => void;
}

// Only the app the panel opened with is looked up; an app bound mid-session
// belongs to the running conversation.
export const KiCodeSession: FC<KiCodeSessionProps> = (props) => {
  const { t } = useTranslation();
  const { onClose } = props;
  // KiCode is only available with a loaded team member (selectAppEnabled).
  const employeeId: string | undefined = useSelector(getAuthTeamMember)?.id;
  const [lookupName] = useState(() => businessAppLookupName(props.session));
  const guardRef = useRef<GuardBusy>(null);
  const resume = useBusinessAppResume(
    lookupName,
    employeeId,
    props.session.resumeId
  );

  const surface = useMemo(
    () => kiCodeSessionSurface(onClose, guardRef, t),
    [onClose, t]
  );
  useEffect(() => surface.mount(), [surface]);
  useEffect(() => {
    if (!resume.pending) surface.chatMounted();
  }, [surface, resume.pending]);

  if (resume.pending) {
    return <ResumePendingPanel qaPrefix={QA_PREFIX} onClose={onClose} />;
  }
  return (
    <KiCodeChat
      {...props}
      guardRef={guardRef}
      resumeId={resume.conversationId}
      linked={lookupName !== null && resume.conversationId !== null}
    />
  );
};

const KiCodeChat: FC<
  KiCodeSessionProps & {
    guardRef: RefObject<GuardBusy>;
    resumeId: string | null;
    /** `resumeId` came from webapp's link rather than this tab's stored resume. */
    linked: boolean;
  }
> = ({
  session,
  guardRef,
  resumeId,
  linked,
  onClose,
  onStartNewConversation,
  onAppCreated,
  onResumable,
}) => {
  const { t } = useTranslation();
  const copy = kiCodeCopy(t);
  const { target, prompt } = session;
  const [start] = useState(() =>
    createKiCodeStart({
      url: DEFAULT_KICODE_URL,
      target,
      newConversation: session.newConversation,
      resumeId,
    })
  );
  const sendRef = useRef<(text: string) => Promise<boolean>>();
  const connectionStateRef = useRef<ConnectionState>('disconnected');
  const [delivery] = useState(() =>
    createPromptDelivery(
      gatedSend(
        () => connectionStateRef.current,
        () => sendRef.current
      )
    )
  );
  const navigation = useKiCodeNavigation(
    hasKiCodeCapability(target, NAVIGATE_CAPABILITY)
  );
  const { appApiName } = target.context;
  const refreshPlugins = useKiCodeRefreshPlugins(
    hasKiCodeCapability(target, REFRESH_PLUGINS_CAPABILITY),
    appApiName
  );
  // Tool frames can arrive after the session binds but before the bound target
  // renders, so frame handlers read the binding from `start`, not the prop.
  const boundApp = useCallback(
    () => start.target().context.appApiName,
    [start]
  );
  const canCapture = hasKiCodeCapability(target, CAPTURE_CAPABILITY);
  const capture = useKiCodeCapture(canCapture, boundApp);
  const openFloatingFrame = useKiCodeOpenFloatingFrame(canCapture, boundApp);
  const { data: businessApps } = useBusinessApps('', {
    enabled: appApiName !== undefined,
    throwOnError: false,
  });
  const appName =
    businessApps?.find((app) => app.api_name === appApiName)?.name ??
    appApiName;

  const chat = useBuilderChat({
    filesWarning: KICODE_FILES_WARNING,
    getStart: start.getStart,
    getLocation: navigation.getLocation,
    onServerMessage: (message, meta) => {
      [
        navigation.handleServerMessage,
        refreshPlugins,
        capture,
        openFloatingFrame,
      ].forEach((handle) => handle(message, meta));
      const refusal = start.handleMessage(message);
      if (message.type === 'session.ready') delivery.onSessionReady();
      const appApiName = boundAppApiName(message);
      if (appApiName) onAppCreated(appApiName);
      const conversationId = start.conversationId();
      if (conversationId && (appApiName || message.type === 'session.ready')) {
        onResumable({ target: start.target(), conversationId });
      }
      if (refusal) {
        clearKiCodeResume();
        // Closing would find the same link on the next open: start fresh.
        if (refusal.close && linked) onStartNewConversation();
        else if (refusal.close) onClose();
      }
    },
  });
  const { sendMessage } = chat.socket;
  sendRef.current = sendMessage;
  connectionStateRef.current = chat.socket.connectionState;
  // A send is refused while the socket isn't ready or an undo/redo restore is
  // running; retry a refused prompt whenever both clear.
  const sendAvailable =
    chat.socket.connectionState === 'ready' &&
    chat.versions.restoringSnapshotId === null;

  useEffect(() => delivery.request(prompt), [delivery, prompt]);
  useEffect(() => {
    if (sendAvailable) delivery.onSendAvailable();
  }, [delivery, sendAvailable]);
  useEffect(() => clearKiCodeResume, []);

  return (
    <>
      <KiCodeChatPanel
        chat={chat}
        qaPrefix={QA_PREFIX}
        title={appName ?? copy.newApp}
        label={copy.label}
        {...kiCodeComposerCopy(copy, target)}
        renderToolRow={(item) => (
          <CaptureToolRow item={item} wsUrl={DEFAULT_KICODE_URL} />
        )}
        onClose={onClose}
        guardRef={guardRef}
        profile={target.profile}
        extraActions={(guardBusy) => (
          <Button
            variant="text"
            color="tertiary"
            leftIcon="action-add"
            leftIconSettings={{ size: 'sm' }}
            onClick={() =>
              guardBusy(onStartNewConversation, copy.newChatConfirm)
            }
            qa={{ id: 'kicode-new-chat' }}
          >
            {copy.newChat}
          </Button>
        )}
      />
      {navigation.element}
    </>
  );
};
